import { useEffect, useState } from "react";
import { api, money, dateOnly, formatApiError, API_BASE } from "@/lib/api";
import { Receipt, X, Download, FileMinus, RotateCcw, Stamp } from "lucide-react";
import { toast } from "sonner";
import HelpTip from "@/components/HelpTip";

const STATUS_OPTIONS = [
    { value: "pendiente", label: "Pendiente" },
    { value: "pago_parcial", label: "Pago parcial" },
    { value: "pagado", label: "Pagado" },
    { value: "vencido", label: "Vencido" },
    { value: "cancelado", label: "Cancelado" },
    { value: "refacturada", label: "Refacturada" },
];

export default function Receivable() {
    const [list, setList] = useState([]);
    const [openPay, setOpenPay] = useState(null);
    const [openStatus, setOpenStatus] = useState(null);
    const [statusValue, setStatusValue] = useState("pendiente");
    const [statusNote, setStatusNote] = useState("");
    const [amount, setAmount] = useState(0);
    const [openNC, setOpenNC] = useState(null); // Nota de crédito
    const [ncForm, setNcForm] = useState({ amount: 0, reason: "", type: "bonificacion" });
    const [openRefact, setOpenRefact] = useState(null);
    const [refactForm, setRefactForm] = useState({ notes: "" });

    const load = async () => {
        try {
            const r = await api.get("/invoices");
            if (r?.data) setList(r.data);
        } catch (e) { console.warn(e); }
    };
    useEffect(() => { load(); }, []);

    const submitPayment = async () => {
        try {
            await api.post("/payments", {
                invoice_id: openPay.id,
                amount: parseFloat(amount),
                payment_date: new Date().toISOString().slice(0, 10),
                method: "transferencia",
            });
            toast.success("Pago registrado");
            setOpenPay(null);
            setAmount(0);
            load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    const submitStatusChange = async () => {
        try {
            await api.patch(`/invoices/${openStatus.id}/status`, { status: statusValue, note: statusNote });
            toast.success(`Estatus actualizado: ${statusValue}`);
            setOpenStatus(null);
            setStatusNote("");
            load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    const submitCreditNote = async () => {
        if (!ncForm.amount || parseFloat(ncForm.amount) <= 0) return toast.error("Captura el monto de la nota");
        if (!ncForm.reason.trim()) return toast.error("Captura el motivo");
        try {
            const r = await api.post(`/invoices/${openNC.id}/credit-note`, {
                amount: parseFloat(ncForm.amount),
                reason: ncForm.reason,
                type: ncForm.type,
            });
            toast.success(`Nota de crédito ${r.data.folio} generada`);
            setOpenNC(null);
            setNcForm({ amount: 0, reason: "", type: "bonificacion" });
            load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    const submitRefacturar = async () => {
        try {
            const r = await api.post(`/invoices/${openRefact.id}/refacturar`, { notes: refactForm.notes });
            toast.success(`Refacturada → ${r.data.folio}`);
            setOpenRefact(null);
            setRefactForm({ notes: "" });
            load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    const timbrar = async (inv) => {
        if (!window.confirm(`Timbrar CFDI de la factura ${inv.folio}?\n\nSi Ecofactura no está configurado, se generará un timbrado MOCK con UUID demo.`)) return;
        try {
            const r = await api.post(`/invoices/${inv.id}/stamp`);
            if (r.data.mocked) toast.success(`Timbrado MOCK · UUID: ${r.data.uuid}`);
            else toast.success(`CFDI timbrado · UUID: ${r.data.uuid}`);
            load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    const total_pending = list.filter((i) => i.status !== "pagado").reduce((s, i) => s + (i.balance || 0), 0);
    const total_overdue = list.filter((i) => i.days_overdue > 0).reduce((s, i) => s + (i.balance || 0), 0);

    return (
        <div className="space-y-4" data-testid="receivable-page">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="fm-card p-4"><div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Total facturado</div><div className="font-mono font-bold text-2xl">{money(list.reduce((s, i) => s + i.total, 0))}</div></div>
                <div className="fm-card p-4 border-l-4 border-l-amber-500"><div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Por cobrar</div><div className="font-mono font-bold text-2xl">{money(total_pending)}</div></div>
                <div className="fm-card p-4 border-l-4 border-l-red-600"><div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Vencido</div><div className="font-mono font-bold text-2xl text-red-600">{money(total_overdue)}</div></div>
            </div>

            <div className="fm-card overflow-hidden">
                <table className="w-full text-sm">
                    <thead><tr className="bg-slate-50 border-b border-slate-200">
                        {["Folio", "Cliente", "Total", "Pagado", "Saldo", "Emisión", "Venc.", "Días", "Estatus", "CFDI", "Acciones", "PDF"].map((h) => (
                            <th key={h} className="text-left py-2.5 px-3 text-[10px] font-bold text-slate-500 uppercase tracking-wider">{h}</th>
                        ))}
                    </tr></thead>
                    <tbody>
                        {list.map((i) => (
                            <tr key={i.id} className="border-b border-slate-100 hover:bg-slate-50">
                                <td className="py-2 px-3 font-mono text-xs font-bold">
                                    {i.folio}
                                    {i.refacturada_a_folio && <div className="text-[10px] text-amber-600">→ {i.refacturada_a_folio}</div>}
                                    {i.credit_note_folio && <div className="text-[10px] text-purple-600">NC {i.credit_note_folio}</div>}
                                </td>
                                <td className="py-2 px-3">{i.client_name}</td>
                                <td className="py-2 px-3 text-right font-mono">{money(i.total)}</td>
                                <td className="py-2 px-3 text-right font-mono text-emerald-700">{money(i.paid_amount)}</td>
                                <td className="py-2 px-3 text-right font-mono font-bold">{money(i.balance)}</td>
                                <td className="py-2 px-3 text-xs font-mono">{dateOnly(i.issue_date)}</td>
                                <td className="py-2 px-3 text-xs font-mono">{dateOnly(i.due_date)}</td>
                                <td className="py-2 px-3">{i.days_overdue > 0 ? <span className="fm-badge fm-badge-critical">{i.days_overdue}d</span> : <span className="fm-badge fm-badge-neutral">Vigente</span>}</td>
                                <td className="py-2 px-3"><button onClick={() => { setOpenStatus(i); setStatusValue(i.status); }} className={`fm-badge cursor-pointer hover:opacity-80 ${i.status === "pagado" ? "fm-badge-positive" : i.status === "pago_parcial" ? "fm-badge-warning" : i.status === "cancelado" || i.status === "refacturada" ? "fm-badge-neutral" : i.status === "vencido" ? "fm-badge-critical" : "fm-badge-neutral"}`} title="Click para cambiar estatus manualmente" data-testid={`status-btn-${i.id}`}>{i.status}</button></td>
                                <td className="py-2 px-3">
                                    {i.cfdi_uuid ? (
                                        <span className="text-[10px] font-mono text-emerald-700" title={i.cfdi_uuid}>
                                            {i.cfdi_mocked ? "MOCK ✓" : "TIMBRADO ✓"}
                                        </span>
                                    ) : (
                                        <button onClick={() => timbrar(i)} className="text-xs text-purple-700 hover:text-purple-900 inline-flex items-center gap-1" title="Timbrar CFDI vía Ecofactura" data-testid={`stamp-btn-${i.id}`}>
                                            <Stamp className="w-3 h-3" /> Timbrar
                                        </button>
                                    )}
                                </td>
                                <td className="py-2 px-3">
                                    <div className="flex items-center gap-2 text-xs">
                                        {i.status !== "pagado" && i.status !== "cancelado" && i.status !== "refacturada" && (
                                            <button onClick={() => { setOpenPay(i); setAmount(i.balance); }} className="text-slate-700 hover:text-red-600 underline" title="Registrar un pago contra esta factura">Pago</button>
                                        )}
                                        {i.status !== "cancelado" && i.status !== "refacturada" && (
                                            <button onClick={() => { setOpenNC(i); setNcForm({ amount: i.balance || i.total, reason: "", type: "bonificacion" }); }} className="text-purple-700 hover:text-purple-900 inline-flex items-center gap-1" title="Generar Nota de Crédito (bonificación, descuento, devolución o cancelación parcial)" data-testid={`nc-btn-${i.id}`}>
                                                <FileMinus className="w-3 h-3" /> Nota crédito
                                            </button>
                                        )}
                                        {i.status !== "refacturada" && (
                                            <button onClick={() => setOpenRefact(i)} className="text-amber-700 hover:text-amber-900 inline-flex items-center gap-1" title="Refacturar — clona con nuevo folio" data-testid={`refact-btn-${i.id}`}>
                                                <RotateCcw className="w-3 h-3" /> Refact.
                                            </button>
                                        )}
                                    </div>
                                </td>
                                <td className="py-2 px-3">
                                    <a href={`${API_BASE}/pdf/invoice/${i.id}`} target="_blank" rel="noreferrer" className="text-xs text-slate-700 hover:text-red-600 inline-flex items-center gap-1 font-bold uppercase tracking-wider" title="Ver / Descargar factura en PDF" data-testid={`pdf-btn-${i.id}`}>
                                        <Download className="w-3.5 h-3.5" /> PDF
                                    </a>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                {list.length === 0 && <div className="p-10 text-center text-sm text-slate-500"><Receipt className="w-10 h-10 mx-auto mb-2 text-slate-300" strokeWidth={1} />Sin facturas.</div>}
            </div>

            {openPay && (
                <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-sm max-w-md w-full">
                        <div className="flex items-center justify-between p-4 border-b border-slate-200">
                            <h3 className="font-display font-bold">Registrar pago</h3>
                            <button onClick={() => setOpenPay(null)}><X className="w-5 h-5" /></button>
                        </div>
                        <div className="p-5">
                            <div className="text-sm mb-3"><span className="text-slate-500">Factura:</span> <span className="font-mono">{openPay.folio}</span></div>
                            <div className="text-sm mb-3"><span className="text-slate-500">Saldo:</span> <span className="font-mono font-bold">{money(openPay.balance)}</span></div>
                            <label className="fm-label">Monto a pagar</label>
                            <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} className="fm-input" />
                            <button onClick={submitPayment} className="fm-btn-accent w-full justify-center mt-3">Registrar pago</button>
                        </div>
                    </div>
                </div>
            )}
            {openStatus && (
                <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-sm max-w-md w-full" data-testid="status-change-modal">
                        <div className="flex items-center justify-between p-4 border-b border-slate-200">
                            <h3 className="font-display font-bold">Cambiar estatus de factura</h3>
                            <button onClick={() => setOpenStatus(null)}><X className="w-5 h-5" /></button>
                        </div>
                        <div className="p-5 space-y-3">
                            <div className="text-sm"><span className="text-slate-500">Factura:</span> <span className="font-mono font-bold">{openStatus.folio}</span></div>
                            <div>
                                <label className="fm-label flex items-center gap-1">Nuevo estatus <HelpTip text="Cambia manualmente el estatus de esta factura. Útil para marcar canceladas (con nota de crédito), vencidas manualmente, etc." /></label>
                                <select value={statusValue} onChange={(e) => setStatusValue(e.target.value)} className="fm-input" data-testid="status-select">
                                    {STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className="fm-label">Nota / Motivo (opcional)</label>
                                <textarea rows={2} value={statusNote} onChange={(e) => setStatusNote(e.target.value)} className="fm-input" placeholder="Ej. Cliente solicita nota de crédito por error en RFC" data-testid="status-note" />
                            </div>
                            <div className="flex gap-2">
                                <button onClick={submitStatusChange} className="fm-btn-primary flex-1 justify-center" data-testid="status-save">Guardar cambio</button>
                                <button onClick={() => setOpenStatus(null)} className="fm-btn-outline">Cancelar</button>
                            </div>
                            <div className="text-[10px] text-slate-500 font-mono">El cambio queda registrado en Auditoría con tu nombre, fecha y motivo.</div>
                        </div>
                    </div>
                </div>
            )}
            {openNC && (
                <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-sm max-w-md w-full" data-testid="credit-note-modal">
                        <div className="flex items-center justify-between p-4 border-b border-slate-200">
                            <h3 className="font-display font-bold flex items-center gap-2"><FileMinus className="w-4 h-4 text-purple-600" /> Nota de crédito</h3>
                            <button onClick={() => setOpenNC(null)}><X className="w-5 h-5" /></button>
                        </div>
                        <div className="p-5 space-y-3">
                            <div className="text-sm"><span className="text-slate-500">Factura origen:</span> <span className="font-mono font-bold">{openNC.folio}</span> · {money(openNC.total)}</div>
                            <div>
                                <label className="fm-label flex items-center gap-1">Tipo <HelpTip text="• Devolución: paciente devuelve servicio • Descuento: ajuste post-emisión • Bonificación: gentileza comercial • Cancelación: anula la factura completa." /></label>
                                <select value={ncForm.type} onChange={(e) => setNcForm({ ...ncForm, type: e.target.value })} className="fm-input" data-testid="nc-type">
                                    <option value="bonificacion">Bonificación</option>
                                    <option value="descuento">Descuento aplicado</option>
                                    <option value="devolucion">Devolución</option>
                                    <option value="cancelacion">Cancelación total</option>
                                </select>
                            </div>
                            <div>
                                <label className="fm-label">Monto (MXN, IVA incluido)</label>
                                <input type="number" min="0" step="0.01" value={ncForm.amount} onChange={(e) => setNcForm({ ...ncForm, amount: e.target.value })} className="fm-input text-right font-mono" data-testid="nc-amount" />
                            </div>
                            <div>
                                <label className="fm-label">Motivo *</label>
                                <textarea rows={2} value={ncForm.reason} onChange={(e) => setNcForm({ ...ncForm, reason: e.target.value })} className="fm-input" placeholder="Ej. Error en RFC, descuento por convenio, cliente reportó cobro doble..." data-testid="nc-reason" />
                            </div>
                            <div className="flex gap-2">
                                <button onClick={submitCreditNote} className="fm-btn-primary flex-1 justify-center" data-testid="nc-save">Emitir nota de crédito</button>
                                <button onClick={() => setOpenNC(null)} className="fm-btn-outline">Cancelar</button>
                            </div>
                            <div className="text-[10px] text-slate-500 font-mono">Queda registrada en Auditoría. Si el monto cubre todo el saldo, la factura pasa a estatus "pagado" o "cancelado".</div>
                        </div>
                    </div>
                </div>
            )}

            {openRefact && (
                <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-sm max-w-md w-full" data-testid="refact-modal">
                        <div className="flex items-center justify-between p-4 border-b border-slate-200">
                            <h3 className="font-display font-bold flex items-center gap-2"><RotateCcw className="w-4 h-4 text-amber-600" /> Refacturar</h3>
                            <button onClick={() => setOpenRefact(null)}><X className="w-5 h-5" /></button>
                        </div>
                        <div className="p-5 space-y-3">
                            <div className="text-sm"><span className="text-slate-500">Factura origen:</span> <span className="font-mono font-bold">{openRefact.folio}</span></div>
                            <div className="bg-amber-50 border-l-4 border-l-amber-400 px-3 py-2 text-xs text-amber-900">
                                Se clonará la factura con un nuevo folio y la actual quedará marcada como <strong>refacturada</strong>. Útil cuando el cliente pide cambiar RFC, uso de CFDI, o corregir datos fiscales.
                            </div>
                            <div>
                                <label className="fm-label">Notas para la nueva factura</label>
                                <textarea rows={2} value={refactForm.notes} onChange={(e) => setRefactForm({ ...refactForm, notes: e.target.value })} className="fm-input" placeholder="Ej. Corrección de RFC del cliente" />
                            </div>
                            <div className="flex gap-2">
                                <button onClick={submitRefacturar} className="fm-btn-primary flex-1 justify-center" data-testid="refact-save">Generar nueva factura</button>
                                <button onClick={() => setOpenRefact(null)} className="fm-btn-outline">Cancelar</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
